import { Component, OnInit } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateService } from '@app/core/services/state.service';
import { CustactCalcService, SubtaskTextItem } from '@app/services/custact-calc.service';
import { debounceTime, Subject } from 'rxjs';

@Component({
    selector: 'tt-custact-calc',
    templateUrl: './custact-calc.component.html',
    styleUrls: ['./custact-calc.component.css'],
})
export class CustactCalcComponent extends ComponentBaseComponent implements OnInit {
    public ttType: string;

    public ttValue: string;

    public get?: CustactCalcGet;

    public selectedCustactCalcOrderTab: string = 'SUBSTASKS';

    public selectedPostTab: string = 'POST';

    public orderNameButtons: FormButton[] = [{ id: 'goto', icon: 'fas fa-chevron-right', type: 'primary', goto: 'orderhead', gotoParms: () => ({ order_internal_no: '' + this.get!.order_internal_no }) }];

    public subtasksTexts: SubtaskTextItem[] = [];

    public grid?: GridOptions;

    public saving = false;

    public updatingCustactCalcPo = false;

    public creatingFromCustomer = false;

    public saveSubtaskTextsSubject = new Subject<void>();

    constructor(state: StateService, private datatask: DataTaskService, private modal: ModalService, private custactCalc: CustactCalcService) {
        super();
        this.ttType = state.getStateParams()['argtype'];
        this.ttValue = state.getStateParams()['argvalue'];
    }

    public async updateCustactCalcPo() {
        if (!this.get) return;

        this.updatingCustactCalcPo = true;

        try {
            await this.custactCalc.updateProductionOrderComponents({ custact_no: this.get.custact_no });
        } catch (error) {
            console.error(error);
        } finally {
            this.updatingCustactCalcPo = false;
        }
    }

    public async createFromCustomer() {
        if (!this.get) return;

        this.creatingFromCustomer = true;

        try {
            const response = await this.custactCalc.createOrder({ custact_no: this.get.custact_no });

            if (response.errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
            } else {
                this.get.order_internal_no = response.order_internal_no;
                this.get.orderline_keyno = response.orderline_keyno;
                this.get.po_keyno = response.po_keyno;
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.creatingFromCustomer = false;
        }
    }

    public async saveSubtasksTexts() {
        if (!this.get) return;

        this.saving = true;

        try {
            await this.custactCalc.saveSubtaskTexts(this.subtasksTexts);
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public onSubtasksChanged() {
        this.saveSubtaskTextsSubject.next();
    }

    public onOptionTypeChanged() {
        this.loadSubtasksTexts();
        this.readGrid();
    }

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid().setRememberId('custactcalc.grid').setLoadDataFunction({ method: 3413, parameters: () => this.get });
            // .setKendoConfig({ height: null });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    public async loadSubtasksTexts() {
        if (!!this.get) {
            this.subtasksTexts = await this.custactCalc.getSubtaskTexts({ custact_no: this.get.custact_no, optiontype: this.get.optiontype });
        }
    }

    private async loadGet() {
        this.get = (await this.datatask.Post(3416, { custact_no: this.ttValue }))[0];
    }

    private async init() {
        await this.loadGet();
        this.onOptionTypeChanged();

        if (this.get?.autosave_subtasktexts_changes === '1') {
            this.saveSubtaskTextsSubject.pipe(debounceTime(500)).subscribe({ next: () => this.saveSubtasksTexts() });
        }
    }

    ngOnInit(): void {
        this.init();
    }
}

interface CustactCalcGet {
    cust_no: string | number;
    cust_name: string;
    cust_name_label: string;
    custact_no: string;
    note: string;
    note_label: string;
    note_placeholder: string;
    project_keyno: string | number;
    project_name: string;
    project_name_label: string;
    projectprosess_keyno: string | number;
    projectprosess_name: string;
    ok: '1' | '0';
    autosave_subtasktexts_changes: '1' | '0';
    activitytype_name: string;
    nbr_of_powtime: string;
    sum_salaryhours: string | number;
    min_date_start: string;
    max_date_end: string;
    deviationtype_keyno: string | number;
    deviationtype_keyno_label: string;
    nbr_of_participants: string | number;
    nbr_of_custactdialogs: string | number;
    nbr_of_childs: string | number;
    edit_custact_possible: '1' | '0';
    solution_note: string;
    show_comments: '1' | '0';
    nbr_of_checklistdata: string | number;
    nbr_of_riskelements: string | number;
    show_riskelements: '1' | '0';
    nbr_of_documents: string | number;
    nbr_of_pictures: string | number;
    nbr_of_deviations: string | number;
    nbr_of_orders: string | number;
    order_internal_no: string | number;
    prepayments_badgeinfo: string;
    show_prepaymentinfo: '1' | '0';
    show_button_timeregister: '1' | '0';
    show_button_materialregister: '1' | '0';
    show_button_materialregister_hour: '1' | '0';
    show_button_deviation: '1' | '0';
    show_button_orders: '1' | '0';
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_forms: '1' | '0';
    show_button_jobcalc: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_button_subtasks: '1' | '0';
    show_button_gantt: '1' | '0';
    show_button_categoridatas: '1' | '0';
    show_button_custactportalusers: '1' | '0';
    show_button_enable_delete: '1' | '0';
    show_setting: '1' | '0';
    order_name: string;
    last_consignment_keyno: string | number;
    show_checkin: '1' | '0';
    nbr_of_checkedin: string | number;
    custactinfotext: string; //Inneholder informasjon om hvilken aktivitet denne tilhører etc,
    quantity: string | number;
    price_locval: string | number;
    price_ordval: string | number;
    linediscount_pst: string | number;
    amount_locval: string | number;
    amount_ordval: string | number;
    text_sales: string;
    text_production: string;
    sortorderno: string | number;
    isoption: '1' | '0';
    cust_telephone: string;
    show_cust_telephone: '1' | '0';
    drawing_no: string;
    prod_id: string;
    custactcalc_specify: '1' | '0';
    nbr_of_parents: string | number;
    po_keyno: string | number;
    orderline_keyno: string | number;
    custact_no_linkto: string | number;
    custactlink_keyno: string | number;
    show_goto_project: '1' | '0';
    nbr_of_custact_customer: string | number;
    isvisible: '1' | '0';
    show_travelbills: '1' | '0';
    nbr_of_travelbills: string | number;
    show_button_map: '1' | '0';
    is_sales_opportunity: '1' | '0';
    show_next_activity: '1' | '0';
    bg_workdetails_group: string;
    actstatus_keyno: string | number;
    activitytype_no: string | number;
    purch_internal_no: string | number;
    purch_order_no: string | number;
    serviceobject_keyno: string | number;
    serviceobject_name: string;
    show_possibleprocesses: '1' | '0';
    nbr_of_custactprocess: string;
    show_custactcomplaint: '1' | '0';
    nbr_of_custactproducts: string | number;
    custact_no_basis: string | number;
    nbr_of_used_in: string | number;
    bg_custactcalcedit_main: string;
    show_button_map3d: '1' | '0';
    microsoft_graph_event_id: string;
    show_send_email: '1' | '0';
    bg_tab1: string;
    webpage_heading: string;
    video_url: string;
    optiontype: string;
    custact_no_belongto: string | number;
    equipment_id: string;
    datetime_start: string;
    datetime_end: string;
    responsible_user_id: string;
    solution_proposal: string;
    solution_note_preventive: string;
    deviation_no: string | number;
    deviationsource_keyno: string | number;
    deviationsource2_keyno: string | number;
    has_deadline: '1' | '0';
    deadline: string;
    sales_size_no: string | number;
    sales_possibility_no: string | number;
    furniturefabrics_id: string;
    model_id: string;
    page_heading: string;
    salarytype_id: string;
    guard: '1' | '0';
    projectactivitytype_id: string;
    show_projectactivitytype_id: '1' | '0';
    businessco_no: string | number;
    department_id: string;
    show_department_id: '1' | '0';
    show_initiated_by_person: '1' | '0';
    initiated_by_person: string | number;
    initiated_by_person_name: string;
    recurring: string | number;
    show_recurring: '1' | '0';
}
